import { useEffect, useMemo, useState } from "react";
import Slider from "../components/Slider/Slider";
import ReviewsSlider from "../components/Slider/ReviewsSlider";
import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import { CategorySection } from "../components/CategorySection";
import { CategoryCard } from "../components/CategoryCard";
import { getCategories } from "../config/categoriesConfig";
import { Link } from "react-router-dom";
import { fetchProducts } from "../services/productService";
import { getImageUrl } from "../utils/imageUrl";
import {
  FiArrowRight,
  FiCheckCircle,
  FiHeadphones,
  FiShield,
  FiTrendingUp,
  FiTruck,
} from "react-icons/fi";
import routes from "../routes";
import "swiper/css";
import "swiper/css/pagination";

const PAGE_KEY_TO_ROUTE = {
  wheatbag: "/wheatbag",
  gift: "/giftProducts",
  kitchen: "/kitchenProducts",
  decor: "/wallarts",
  handmade: "/handmadeProducts",
  natural: "/naturalProducts",
  camping: "/campingOutdoors",
  fridge: "/fridgeDeo",
  japanese: "/japaneseProducts",
};

const resolveCollectionRoute = (product) => {
  const placements = Array.isArray(product?.pagePlacements)
    ? [...product.pagePlacements]
    : [];

  const firstNonHomePlacementKey = placements
    .sort(
      (a, b) => Number(a?.sort_order ?? 9999) - Number(b?.sort_order ?? 9999),
    )
    .map((placement) => String(placement?.page_key || "").toLowerCase())
    .find((key) => key && key !== "home");

  if (firstNonHomePlacementKey && PAGE_KEY_TO_ROUTE[firstNonHomePlacementKey]) {
    return PAGE_KEY_TO_ROUTE[firstNonHomePlacementKey];
  }

  const normalizedTags = Array.isArray(product?.tags)
    ? product.tags.map((tag) => String(tag).toLowerCase())
    : [];

  if (normalizedTags.some((tag) => tag.includes("wheat"))) {
    return PAGE_KEY_TO_ROUTE.wheatbag;
  }
  if (normalizedTags.some((tag) => tag.includes("japan"))) {
    return PAGE_KEY_TO_ROUTE.japanese;
  }
  if (normalizedTags.some((tag) => tag.includes("fridge"))) {
    return PAGE_KEY_TO_ROUTE.fridge;
  }

  return routes.products;
};

function Home() {
  const { t, i18n } = useTranslation(); // Destructure the t function
  const categories = getCategories(t);
  const [bestSellers, setBestSellers] = useState([]);
  const [bestSellerLoading, setBestSellerLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadBestSellers = async () => {
      try {
        setBestSellerLoading(true);
        const data = await fetchProducts();

        const ranked = [...(Array.isArray(data) ? data : [])]
          .map((product) => {
            const placements = Array.isArray(product.pagePlacements)
              ? product.pagePlacements
              : [];
            const homePlacement = placements.find(
              (placement) => placement.page_key === "home",
            );

            return {
              ...product,
              _featured: homePlacement?.is_featured ? 1 : 0,
              _placementOrder: Number(homePlacement?.sort_order ?? 9999),
              _stockScore: Number(product.stock_quantity ?? 0),
            };
          })
          .sort((a, b) => {
            if (b._featured !== a._featured) return b._featured - a._featured;
            if (a._placementOrder !== b._placementOrder) {
              return a._placementOrder - b._placementOrder;
            }
            return b._stockScore - a._stockScore;
          });

        if (isMounted) {
          setBestSellers(ranked);
        }
      } catch (error) {
        if (isMounted) setBestSellers([]);
      } finally {
        if (isMounted) setBestSellerLoading(false);
      }
    };

    loadBestSellers();

    return () => {
      isMounted = false;
    };
  }, []);

  const bestSellerCards = useMemo(() => {
    return bestSellers.map((product) => {
      const productName = product[`name_${i18n.language}`] || product.name_en;
      const imageUrl =
        product.images && product.images[0]?.image_url
          ? getImageUrl(product.images[0].image_url)
          : "https://placehold.co/480x360/F3E5EA/6F5B64?text=Product";

      return {
        id: product.product_id,
        name: productName,
        imageUrl,
        price: `${product.price} TL`,
        targetPath: resolveCollectionRoute(product),
      };
    });
  }, [bestSellers, i18n.language]);

  return (
    <div className="min-h-screen">
      <Slider />

      {/* Background Image Section */}
      <div className="canvas-background flex flex-col items-center justify-center min-h-[50vh]">
        {/* the div for the welcome to crossborder */}
        <div className="md:px-0 px-[2em] flex flex-col items-center justify-center space-y-3 mb-5 mt-5">
          <img
            src="/images/handmadeLogo.png"
            alt="Logo"
            className="h-40 lg:mt-8 mt-4 "
          />
          <h1 className="mt-6 text-center text-[1.35rem] sm:text-3xl md:text-4xl leading-[1.35] md:leading-none font-[500] mb-4 text-[#E06154] font-raleway">
            {t("homePageWelcomeDiv.title")}
          </h1>
          <p className="text-[#B36C5E] text-center max-w-prose mx-auto font-normal font-raleway">
            {t("homePageWelcomeDiv.subtitle")}
          </p>
          <Link
            to={routes.products}
            className="!mt-6 items-center font-[lighter] flex text-white tracking-[0.1em] md:h-9 h-[30px] text-[0.8em] bg-[#E06154] font-mukta px-6 py-2 transition-all duration-300 hover:scale-105"
          >
            {t("homePageWelcomeDiv.button")}
          </Link>
        </div>
        {/* categories */}
        <CategorySection title={t("categories.chooseCategory1")} />
        <div className="w-[90%] grid grid-cols-3 md:grid-cols-8  md:gap-[2.5rem] gap-6 mb-4">
          {categories.map((category) => (
            <CategoryCard
              key={category.id}
              title={category.name}
              imageSrc={category.imageSrc}
              to={category.to}
            />
          ))}
        </div>

        {/* About section */}
        <div className="canvas-backgroundPink  flex flex-col md:flex-row mt-14 w-full min-h-[40em] shadow-left-separator">
          {/* Text Section */}
          <div className="font-raleway text-white font-light text-base md:w-[45%] flex items-center md:items-start justify-center flex-col space-y-6 px-10 py-10 text-center md:text-left">
            <h1 className="font-[500]">{t("aboutSection.block1.title")}</h1>
            <p className="text-[small] md:text-[medium]">
              {t("aboutSection.block1.paragraph")}
            </p>
            <Link
              to={routes.aboutUs}
              className="items-center font-light flex text-white tracking-[0.1em] mt-4 text-sm bg-[#B17075] 
                font-mukta px-6 py-2 transition-all duration-300 hover:scale-105"
            >
              {t("aboutSection.block1.button")}
            </Link>
          </div>

          {/* Image Section */}
          <div className="w-full md:w-[55%] min-h-[300px] md:min-h-full about-background bg-cover bg-center bg-no-repeat box-content" />
        </div>

        {/* Block 2 */}
        <div className="mb-14 canvas-backgroundBlue flex flex-col md:flex-row w-full min-h-[40em] shadow-[-4px_0_28px_0px_#5d2d2d]">
          {/* Background Image Section */}
          <div className="w-full md:w-[55%] min-h-[300px] md:min-h-full about-background2 bg-cover bg-center bg-no-repeat box-content " />

          {/* <div className="about-background2 w-full md:w-[60%] min-h-[300px] md:min-h-full bg-cover bg-center bg-no-repeat shadow-right-separator" /> */}

          {/* Text Section - 40% on desktop */}
          <div className="font-raleway text-white font-light text-base w-full md:w-[40%] flex items-center md:items-start justify-center flex-col space-y-6 px-10 py-10 relative text-center md:text-left">
            <h1 className="font-[500]">{t("aboutSection.block2.title")}</h1>
            <p>{t("aboutSection.block2.paragraph")}</p>
            <Link
              to={routes.naturalProducts}
              className="items-center font-light flex text-white tracking-[0.1em] mt-4 text-sm bg-[#BB7847] 
              font-mukta px-6 py-2 transition-all duration-300 hover:scale-105"
            >
              {t("aboutSection.block2.button")}
            </Link>

            {/* Leaf image absolutely positioned */}
            <img
              className="absolute bottom-4 right-[1%] md:!right-[-4rem] w-[80px] md:w-[100px]"
              src="/images/leaf.png"
              alt=""
            />
          </div>
        </div>

        {/* Best sellers teaser */}
        <section
          className="relative w-full px-4 py-14  sm:px-8 overflow-hidden  [background-attachment:scroll] md:[background-attachment:fixed]"
          style={{
            backgroundImage: 'url("/images/canvas.webp")',
            backgroundPosition: "center",
            backgroundRepeat: "repeat",
            backgroundSize: "1000px ",
            backgroundColor: "rgb(128 70 70)",
          }}
        >
          <div className="pointer-events-none absolute inset-0 " />
          <div className="pointer-events-none absolute -left-16 top-10 h-52 w-52 rounded-full bg-[#f2dfe7]/70 blur-3xl" />
          <div className="pointer-events-none absolute -right-16 bottom-10 h-52 w-52 rounded-full bg-[#efe6cb]/60 blur-3xl" />

          <div className="relative place-self-center w-[90%] z-10 overflow-hidden rounded-[2rem] border border-white/70 bg-white/55 shadow-[0_34px_90px_-58px_rgba(109,58,74,0.5)] backdrop-blur-[1px]">
            <div className="flex flex-col gap-6 p-6 sm:p-8 lg:flex-row lg:items-end lg:justify-between lg:items-center lg:p-10">
              <div className="text-center lg:text-left">
                <p className="inline-flex items-center gap-2 rounded-full border border-[#ead5dd] bg-white/80 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#a0647b]">
                  <FiTrendingUp className="text-sm" />
                  {t("homePage.bestSellers.badge")}
                </p>
                <h2 className="mt-3 text-2xl sm:text-3xl font-black leading-tight text-[#7e3957] md:text-4xl">
                  {t("homePage.bestSellers.title")}
                </h2>
                <p className="mt-3 max-w-2xl text-sm leading-7 text-[#6f5b64] md:text-base">
                  {t("homePage.bestSellers.description")}
                </p>

                <div className="mt-5 flex flex-wrap gap-2 justify-center lg:justify-start">
                  <span className="w-[70%] lg:w-auto rounded-full border border-[#ecdbe2] bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#87506a]">
                    {t("homePage.bestSellers.tag1")}
                  </span>
                  <span className="w-[70%] lg:w-auto rounded-full border border-[#ecdbe2] bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#87506a]">
                    {t("homePage.bestSellers.tag2")}
                  </span>
                </div>
              </div>

              <Link
                to={routes.products}
                className="self-center lg:self-auto inline-flex items-center gap-2 rounded-full bg-[#83375B] px-6 py-3 text-sm font-bold tracking-[0.12em] text-white transition-all duration-300 hover:scale-[1.03] hover:bg-[#6d2c4d] shadow-[0_14px_34px_-16px_rgba(96,39,68,0.7)]"
              >
                {t("homePage.bestSellers.cta")}
                <FiArrowRight />
              </Link>
            </div>

            <div className="pb-8 lg:pb-10 px-6 sm:px-8 lg:px-10">
              {bestSellerLoading ? (
                <div className="flex gap-4 overflow-hidden">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div
                      key={`best-loader-${index}`}
                      className="w-[78vw] max-w-[270px] shrink-0 animate-pulse overflow-hidden rounded-2xl border border-[#efe0e6] bg-white/90"
                    >
                      <div className="h-36 bg-[#f2e2e8]" />
                      <div className="space-y-2 p-4">
                        <div className="h-4 rounded-full bg-[#f2e6eb]" />
                        <div className="h-4 w-2/3 rounded-full bg-[#f6edf1]" />
                        <div className="h-9 rounded-xl bg-[#efe2e8]" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Swiper
                  dir="ltr"
                  modules={[Autoplay, Pagination]}
                  autoplay={{
                    delay: 3200,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true,
                  }}
                  pagination={{ clickable: true, dynamicBullets: true }}
                  spaceBetween={16}
                  slidesPerView={1.15}
                  breakpoints={{
                    640: { slidesPerView: 2.15, spaceBetween: 18 },
                    1024: { slidesPerView: 3.15, spaceBetween: 20 },
                    1280: { slidesPerView: 4, spaceBetween: 22 },
                  }}
                  className="best-sellers-swiper !pb-12"
                >
                  {bestSellerCards.map((item) => (
                    <SwiperSlide key={item.id} className="!h-auto">
                      <article className="group h-full overflow-hidden rounded-2xl border border-[#efe0e6] bg-white/95 shadow-[0_12px_30px_-20px_rgba(109,58,74,0.55)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_22px_42px_-22px_rgba(109,58,74,0.6)]">
                        <div className="relative h-40 overflow-hidden">
                          <img
                            src={item.imageUrl}
                            alt={item.name}
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                          <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/30 to-transparent" />
                        </div>

                        <div className="p-4">
                          <h3 className="line-clamp-2 min-h-[2rem] text-sm font-semibold text-[#3a2a32]">
                            {item.name}
                          </h3>
                          <p className="mt-2 text-base font-black text-[#83375B]">
                            {item.price}
                          </p>

                          <Link
                            to={item.targetPath}
                            className="mt-4 flex w-full items-center justify-center rounded-xl bg-[#f6edf1] px-3 py-2.5 text-sm font-semibold text-[#7e3957] transition-colors hover:bg-[#eddce5] hover:text-[#65344a]"
                          >
                            {t("product.details")}
                          </Link>
                        </div>
                      </article>
                    </SwiperSlide>
                  ))}
                </Swiper>
              )}
            </div>
          </div>
        </section>

        {/* Trust and shipping policy section */}
        <section className="w-[90%] px-4 pt-14 sm:px-8 ">
          <div className="  overflow-hidden rounded-[2rem] border border-[#ead4d8] bg-[linear-gradient(145deg,#fff7f8_0%,#fffefb_55%,#fdf1ed_100%)] shadow-[0_30px_80px_-55px_rgba(109,58,74,0.45)]">
            <div className="grid gap-8 p-6 sm:p-8 lg:grid-cols-[1.1fr_0.9fr] lg:p-10">
              <div className="text-center lg:text-left">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#b17075]">
                  {t("homePage.trustPolicy.badge", "Customer Trust")}
                </p>
                <h2 className="mt-3 text-2xl sm:text-3xl font-black leading-tight text-[#7e3957] md:text-4xl">
                  {t(
                    "homePage.trustPolicy.title",
                    "Shipping policy and buyer protection, made clear.",
                  )}
                </h2>
                <p className="mt-4 max-w-2xl text-sm leading-7 text-[#6f5b64] md:text-base">
                  {t(
                    "homePage.trustPolicy.description",
                    "We keep delivery details transparent, secure your payments, and support you after purchase. Shop with confidence knowing what to expect at every step.",
                  )}
                </p>

                <div className="mt-7 flex flex-wrap gap-3 justify-center lg:justify-start">
                  <span className="w-[70%] lg:w-auto transition-transform hover:scale-[1.05] rounded-full border border-[#edd7de] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#7e3957]">
                    {t(
                      "homePage.trustPolicy.chips.fastDispatch",
                      "Fast dispatch",
                    )}
                  </span>
                  <span className="w-[70%] lg:w-auto transition-transform hover:scale-[1.05] rounded-full border border-[#edd7de] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#7e3957]">
                    {t(
                      "homePage.trustPolicy.chips.trackedDelivery",
                      "Tracked delivery",
                    )}
                  </span>
                  <span className="w-[70%] lg:w-auto transition-transform hover:scale-[1.05] rounded-full border border-[#edd7de] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#7e3957]">
                    {t(
                      "homePage.trustPolicy.chips.secureCheckout",
                      "Secure checkout",
                    )}
                  </span>
                </div>

                <Link
                  to={routes.contactUs}
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#83375B] px-6 py-3 text-sm font-bold tracking-[0.12em] text-white transition-all duration-300 hover:scale-[1.03] hover:bg-[#6d2c4d]"
                >
                  {t("homePage.trustPolicy.contactSupport", "Contact Support")}
                </Link>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 ">
                <div className="rounded-2xl border border-white/70 bg-white/85 p-5 shadow-sm transition-transform hover:scale-[1.05] hover:-rotate-2 text-center sm:text-left">
                  <FiTruck className="mx-auto sm:mx-0 text-2xl text-[#83375B]" />
                  <h3 className="mt-3 text-lg font-bold text-[#7e3957]">
                    {t(
                      "homePage.trustPolicy.cards.shipping.title",
                      "Shipping Policy",
                    )}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-[#6f5b64]">
                    {t(
                      "homePage.trustPolicy.cards.shipping.description",
                      "Delivery windows and handling times are clearly communicated before checkout.",
                    )}
                  </p>
                </div>

                <div className="rounded-2xl border border-white/70 bg-white/85 p-5 shadow-sm transition-transform hover:scale-[1.05] hover:rotate-2 text-center sm:text-left">
                  <FiShield className="mx-auto sm:mx-0 text-2xl text-[#83375B]" />
                  <h3 className="mt-3 text-lg font-bold text-[#7e3957]">
                    {t(
                      "homePage.trustPolicy.cards.payments.title",
                      "Secure Payments",
                    )}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-[#6f5b64]">
                    {t(
                      "homePage.trustPolicy.cards.payments.description",
                      "Encrypted checkout and trusted payment providers keep your details protected.",
                    )}
                  </p>
                </div>

                <div className="rounded-2xl border border-white/70 bg-white/85 p-5 shadow-sm transition-transform hover:scale-[1.05] hover:-rotate-2 text-center sm:text-left">
                  <FiCheckCircle className="mx-auto sm:mx-0 text-2xl text-[#83375B]" />
                  <h3 className="mt-3 text-lg font-bold text-[#7e3957]">
                    {t(
                      "homePage.trustPolicy.cards.returns.title",
                      "Returns & Care",
                    )}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-[#6f5b64]">
                    {t(
                      "homePage.trustPolicy.cards.returns.description",
                      "If there is an issue with your order, our team helps resolve it quickly and fairly.",
                    )}
                  </p>
                </div>

                <div className="rounded-2xl border border-white/70 bg-white/85 p-5 shadow-sm transition-transform hover:scale-[1.05] hover:rotate-2 text-center sm:text-left">
                  <FiHeadphones className="mx-auto sm:mx-0 text-2xl text-[#83375B]" />
                  <h3 className="mt-3 text-lg font-bold text-[#7e3957]">
                    {t(
                      "homePage.trustPolicy.cards.support.title",
                      "Real Support",
                    )}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-[#6f5b64]">
                    {t(
                      "homePage.trustPolicy.cards.support.description",
                      "Questions before or after purchase? You can always reach us directly.",
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ teaser with policy link */}
        <section className="w-[90%] px-4 pt-8 sm:px-8">
          <div className="bg-[linear-gradient(145deg,#fff7f8_0%,#fffefb_55%,#fdf1ed_100%)] shadow-[0_30px_80px_-55px_rgba(109,58,74,0.45)] rounded-[2rem] border border-[#ead4d8] bg-white shadow-[0_24px_70px_-54px_rgba(109,58,74,0.45)] overflow-hidden">
            <div className="grid gap-6 p-6 sm:p-8 lg:grid-cols-[1.05fr_0.95fr] lg:p-10">
              <div className="text-center lg:text-left">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#b17075]">
                  {t("homePage.faqTeaser.badge", "FAQ Teaser")}
                </p>
                <h2 className="mt-3 text-[1.45rem] sm:text-3xl font-black text-[#7e3957] leading-tight">
                  {t(
                    "homePage.faqTeaser.title",
                    "Questions about shipping, returns, or support?",
                  )}
                </h2>
                <p className="mt-4 text-sm sm:text-base leading-7 text-[#6f5b64] max-w-xl mx-auto lg:mx-0">
                  {t(
                    "homePage.faqTeaser.description",
                    "We made our policies simple and transparent so customers can shop confidently. See quick answers below, then open the full policy page for details.",
                  )}
                </p>

                <Link
                  to={routes.policies}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#83375B] px-6 py-3 text-sm font-bold tracking-[0.12em] text-white transition-all duration-300 hover:scale-[1.03] hover:bg-[#6d2c4d]"
                >
                  {t("homePage.faqTeaser.viewPolicy", "View Full Policy")}
                  <FiArrowRight />
                </Link>
              </div>

              <div className="grid gap-3">
                <div className="rounded-xl border border-[#f1e2e7] bg-[#fffafb] p-4 hover:-translate-y-1 transition-transform text-center sm:text-left">
                  <p className="font-semibold text-[#7e3957] text-sm">
                    {t("homePage.faqTeaser.items.q1", "How fast is shipping?")}
                  </p>
                  <p className="mt-1 text-sm text-[#6f5b64]">
                    {t(
                      "homePage.faqTeaser.items.a1",
                      "Most orders dispatch within 1-2 business days.",
                    )}
                  </p>
                </div>
                <div className="rounded-xl border border-[#f1e2e7] bg-[#fffafb] p-4 hover:-translate-y-1 transition-transform text-center sm:text-left">
                  <p className="font-semibold text-[#7e3957] text-sm">
                    {t("homePage.faqTeaser.items.q2", "Can I track my order?")}
                  </p>
                  <p className="mt-1 text-sm text-[#6f5b64]">
                    {t(
                      "homePage.faqTeaser.items.a2",
                      "Yes, tracking details are shared once your order is shipped.",
                    )}
                  </p>
                </div>
                <div className="rounded-xl border border-[#f1e2e7] bg-[#fffafb] p-4 hover:-translate-y-1 transition-transform text-center sm:text-left">
                  <p className="font-semibold text-[#7e3957] text-sm">
                    {t(
                      "homePage.faqTeaser.items.q3",
                      "Need help after purchase?",
                    )}
                  </p>
                  <p className="mt-1 text-sm text-[#6f5b64]">
                    {t(
                      "homePage.faqTeaser.items.a3",
                      "Our support team helps with delivery and product issues.",
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <CategorySection title={t("categories.chooseCategory2")} />
        <div className="lg:mb-[-25px] mb-[-15px] group cursor-pointer w-[100%] flex flex-row justify-center flex-wrap gap-0 md:gap-[2.5rem] mb-4">
          <a
            href="https://ty.gl/1ifct8gwqgirr"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="/images/trendyolLogo.png"
              alt="Trendyol Logo"
              className="lg:h-40 h-32 transition-transform duration-300 hover:scale-110"
            />
          </a>

          <a
            href="https://www.amazon.com.tr/stores/CrossBorder/page/26E68055-F625-4167-A0BA-1037467FDA29?ref_=cm_sw_r_ud_ast_store_XXSD2VHSCKEGQADWAC1M"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="/images/amazonLogo.png"
              alt="Amazon Logo"
              className="lg:h-40 h-32 transition-transform duration-300 hover:scale-110"
            />
          </a>

          <a
            href="https://www.amazon.co.jp/CROSS-BORDER-%E3%83%AA%E3%83%A9%E3%83%83%E3%82%AF%E3%82%B9%E7%94%A8%EF%BD%9C%E3%83%A9%E3%83%99%E3%83%B3%E3%83%80%E3%83%BC%E3%81%AE%E9%A6%99%E3%82%8A-%E3%83%9B%E3%83%83%E3%83%88%EF%BC%86%E3%82%B3%E3%83%BC%E3%83%AB%E3%83%89%E3%83%91%E3%83%83%E3%82%AF%EF%BD%9C%E9%A6%96%E3%83%BB%E8%82%A9%E3%83%BB%E8%85%B0%E3%81%BE%E3%82%8F%E3%82%8A%E7%94%A8%EF%BD%9C100-%E3%82%B3%E3%83%83%E3%83%88%E3%83%B3%EF%BD%9C%E6%97%A5%E6%9C%AC%E8%A3%BD-%E3%83%8F%E3%83%B3%E3%83%89%E3%83%A1%E3%82%A4%E3%83%89%EF%BC%8850%C3%9715cm%EF%BC%89/dp/B0GJM8FLLX/ref=sr_1_6?dib=eyJ2IjoiMSJ9.mP9tn5y3PMZgsKex3v7J-Jpf0f9uOfDCTT30vfGer3qJOov6tnbtwq9nQvnXG60cAl5C3-y59wRSuiZHvFe2H4-_RUf26n1M2-KJUlQGdZlzmtI_0F2nutgAX_lwmi-czG0aOaPEV7Erbwg_PFj1nnCHpxhZWfkc1nDde2ydKHJbJBLA7s0vJCWLMOHRDMsh3dHopplFdHOraE0aZ9qjrFVgVz7wA6ggu1ImHRg9GbeEZGP1n-FS9dzbOK3X8Lk7MJ57WJ7rUS0bYxjckZRr8xWNQ-ygMfTi0Ixbd4lzaQM.wKTPD0oN1AzGhua_t5_J6fgOyyTr4zaECEzNbBcYW0M&dib_tag=se&keywords=CROSS%2BBORDER&qid=1776181530&sr=8-6&th=1"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="/images/amazonJPLogo.png"
              alt="Amazon Japan Logo"
              className="lg:h-40 h-32 transition-transform duration-300 hover:scale-110"
            />
          </a>

          <a
            href="https://www.hepsiburada.com/magaza/crossborder-store"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="/images/hepsiLogo.png"
              alt="Hepsiburada Logo"
              className="lg:h-40 h-32 transition-transform duration-300 hover:scale-110"
            />
          </a>

          <a
            href="https://www.ciceksepeti.com/your-shop-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="/images/cicekLogo.png"
              alt="ÇiçekSepeti Logo"
              className="lg:h-40 h-32 transition-transform duration-300 hover:scale-110"
            />
          </a>
        </div>

        <CategorySection
          className="md:mt-5"
          title={t("categories.chooseCategory3")}
        />
        <ReviewsSlider />
      </div>
    </div>
  );
}

export default Home;
